import React from 'react';
import { useTranslation } from 'react-i18next';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './command';
import { Square, Circle, Triangle, Diamond, Hexagon, Pentagon, Star, User, Building2, Database, MessageSquare } from 'lucide-react';
import { AI_FLOWCHART_SHAPES, AI_FLOWCHART_SHAPE_IDS } from '../../domains/process/shapes/flowchart/FlowchartShapes.defs';
import {
  ORG_PERSON_MATCH_SOURCE_SHAPE_ID,
  ORG_PERSON_MATCH_SOURCE_SHAPE_LABEL,
  ORG_SHAPE_CATALOG,
} from '../../domains/orgchart/models/orgShapeCatalog';

type Direction = 'top' | 'right' | 'bottom' | 'left';

interface ShapeQuickCreatePanelProps {
  shapeType: 'basic-shape' | 'flowchart-shape' | 'bpmn-shape' | 'org-shape';
  direction: Direction;
  onCreateNew: (params: { variant: string }) => void;
  allowedVariants?: string[];
  /** On projected people charts a new position is a vacancy, drawn as an open seat. */
  positionAsOpenSeat?: boolean;
}

const toTitleCaseFromId = (value: string): string =>
  value
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const createAiFlowchartIcon = (shapeId: string): React.FC<{ className?: string }> => {
  const def = (AI_FLOWCHART_SHAPES as Record<string, { width: number; height: number; strokeWidth?: number; paths: string[] }>)[shapeId];
  return ({ className }) => (
    <svg
      viewBox={`0 0 ${def?.width ?? 24} ${def?.height ?? 24}`}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={String(def?.strokeWidth ?? 1.6)}
      strokeLinecap="butt"
      strokeLinejoin="miter"
    >
      {(def?.paths ?? []).map((d, idx) => (
        <path
          key={`ai-flowchart-quick-create-icon-path-${shapeId}-${idx}`}
          d={d}
          fillRule="evenodd"
          clipRule="evenodd"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
};

const basicShapeDefs = [
  { value: 'rectangle', icon: Square },
  { value: 'circle', icon: Circle },
  { value: 'triangle', icon: Triangle },
  { value: 'diamond', icon: Diamond },
  { value: 'hexagon', icon: Hexagon },
  { value: 'pentagon', icon: Pentagon },
  { value: 'star', icon: Star },
] as const;

const flowchartPrimaryShapeIds = new Set(['process', 'decision', 'start-end', 'input-output', 'document']);

const bpmnShapeDefs = [
  { value: 'bpmn-event', icon: Circle },
  { value: 'bpmn-gateway', icon: Diamond },
  { value: 'bpmn-activity', icon: Square },
  { value: 'bpmn-data', icon: Square },
  { value: 'bpmn-data-store', icon: Database },
  { value: 'bpmn-annotation', icon: MessageSquare },
] as const;

const ORG_SHAPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  org_person: User,
  org_person_photo: User,
  org_person_photo_left: User,
  org_person_photo_right: User,
  org_person_photo_inner_left: User,
  org_person_text: User,
  org_position: Square,
  org_department: Building2,
};

export const ShapeQuickCreatePanel: React.FC<ShapeQuickCreatePanelProps> = ({
  shapeType,
  onCreateNew,
  allowedVariants,
  positionAsOpenSeat = false
}) => {
  const { t } = useTranslation('diagram');
  const [search, setSearch] = React.useState('');

  const orgchartShapeOptions = React.useMemo(
    () =>
      [
        {
          value: ORG_PERSON_MATCH_SOURCE_SHAPE_ID,
          label: t(`orgchart.quickCreate.${ORG_PERSON_MATCH_SOURCE_SHAPE_ID}`, {
            defaultValue: ORG_PERSON_MATCH_SOURCE_SHAPE_LABEL,
          }) as string,
          icon: ORG_SHAPE_ICONS[ORG_PERSON_MATCH_SOURCE_SHAPE_ID] ?? User,
        },
        ...ORG_SHAPE_CATALOG.map((shape) => ({
          value: shape.id,
          label: (positionAsOpenSeat && shape.id === 'org_position'
            ? t('orgchart.quickCreate.org_open_seat', { defaultValue: 'Open seat' })
            : t(`orgchart.quickCreate.${shape.id}`, { defaultValue: shape.label })) as string,
          icon: ORG_SHAPE_ICONS[shape.id] ?? Square,
        })),
      ],
    [positionAsOpenSeat, t],
  );

  const basicShapeOptions = React.useMemo(
    () =>
      basicShapeDefs.map((def) => ({
        value: def.value,
        label: t(`shapes.basicLabels.${def.value}`) as string,
        icon: def.icon,
      })),
    [t]
  );

  const flowchartAllShapeOptions = React.useMemo(
    () =>
      AI_FLOWCHART_SHAPE_IDS.map((id) => ({
        value: id,
        label: t(`shapes.flowchartLabels.${id}`, { defaultValue: toTitleCaseFromId(id) }) as string,
        icon: createAiFlowchartIcon(id),
      })),
    [t]
  );

  const bpmnShapeOptions = React.useMemo(
    () =>
      bpmnShapeDefs.map((def) => ({
        value: def.value,
        label: t(`shapePicker.bpmnShapes.${def.value}`) as string,
        icon: def.icon,
      })),
    [t]
  );

  const shapeOptions = React.useMemo(() => {
    switch (shapeType) {
      case 'basic-shape':
        return basicShapeOptions;
      case 'flowchart-shape':
        if (!search) {
          return flowchartAllShapeOptions.filter((shape) => flowchartPrimaryShapeIds.has(shape.value));
        }
        return flowchartAllShapeOptions;
      case 'bpmn-shape':
        return bpmnShapeOptions;
      case 'org-shape':
        return orgchartShapeOptions;
      default:
        return [];
    }
  }, [shapeType, search, basicShapeOptions, flowchartAllShapeOptions, bpmnShapeOptions, orgchartShapeOptions]);

  const filteredShapes = React.useMemo(() => {
    if (!search) return shapeOptions;
    return shapeOptions.filter(shape =>
      shape.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, shapeOptions]);

  const visibleShapes = React.useMemo(() => {
    if (!allowedVariants?.length) return filteredShapes;
    const allow = new Set(allowedVariants);
    return filteredShapes.filter((shape) => allow.has(shape.value));
  }, [allowedVariants, filteredShapes]);

  const handleShapeSelect = (variant: string) => {
    onCreateNew({
      variant
    });
  };

  return (
    <div className="w-[260px]">
      <Command>
        <CommandInput
          placeholder={t('shapes.searchPlaceholder')}
          value={search}
          onValueChange={setSearch}
          autoFocus
          className="h-8 text-xs"
        />
        <CommandEmpty>
          {search ? t('quickCreatePanel.noMatches') : null}
        </CommandEmpty>

        <CommandGroup heading={t('tools.shapes')}>
          {visibleShapes.map((shape) => {
            const Icon = shape.icon;
            return (
              <CommandItem
                key={shape.value}
                value={`${shape.value} ${shape.label}`}
                onSelect={() => handleShapeSelect(shape.value)}
                className="text-xs cursor-pointer"
                data-testid={`shape-quick-create-${shape.value}`}
              >
                <Icon className="mr-2 h-4 w-4" />
                <span>{shape.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </Command>
    </div>
  );
};
